import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Pressable, StyleSheet } from 'react-native';
import { useStyle } from 'react-native-style-utilities';
import {
  FallbackImage,
  IllusEmpty_2,
  LoadingPlaceholder,
  theme,
  DecorativeText,
} from '@sg/garnish';

export const GiftCardHeader = (props: GiftCardHeaderProps) => {
  const {
    isFetchingContent,
    headerTitle,
    headerBackgroundColor,
    headerTextColor,
    headerImage,
    onPress,
  } = props;

  const { formatMessage } = useIntl();

  const textStyle = useStyle(() => ({ color: headerTextColor }), [headerTextColor] ); // prettier-ignore
  const backgroundStyle = useStyle(() => ({ backgroundColor: headerBackgroundColor }), [headerBackgroundColor]); // prettier-ignore

  // ─── Rendering ───────────────────────────────────────────────────────────────

  if (isFetchingContent) {
    return (
      <LoadingPlaceholder
        palette="lightestKale"
        borderRadius={theme.radius.large}
        rowHeight={108}
      />
    );
  }

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={formatMessage(messages.a11yLabel)}
      style={[styles.container, backgroundStyle]}
      onPress={onPress}
    >
      <DecorativeText sizeMatch={['24']} style={textStyle}>
        {headerTitle}
      </DecorativeText>

      <FallbackImage
        style={styles.image}
        baseUrl={headerImage}
        defaultImage={IllusEmpty_2}
        contentfulOptions={contentfulOptions}
      />
    </Pressable>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const contentfulOptions = {
  fm: 'webp' as const,
  q: 90,
  w: 80,
  h: 60,
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  a11yLabel: {
    defaultMessage: 'Open gift cards purchase screen',
    description: 'Gift Cards Screen | Header | A11y label',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing['6'],
    padding: theme.spacing['6'],
    borderRadius: theme.radius.large,
  },
  image: {
    width: 80,
    height: 60,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type GiftCardHeaderProps = {
  isFetchingContent: boolean;
  headerTitle: string;
  headerBackgroundColor: string;
  headerTextColor: string;
  headerImage?: string;
  onPress: () => void;
};
